export interface Gift {
  desktopImage: string;
  giftAdditionalInformation: string;
  giftTitle: string;
  giftTitlePreposition: string;
  giftUid: string;
  mainGift: boolean;
  mobileImage: string;
}

export interface ExclusiveOffer {
  description: string;
  endDate: string;
  endTime: string;
  entryUid: string;
  exclusiveOfferType: string;
  offerUid: string;
  offerTitle: string;
  startDate: string;
  startTime: string;
  urlIdentifiers: string[];
  productReferences: string[];
  introduction: string;
  includeCountdownTimer: boolean;
  bannerDesktopImage: string;
  bannerModileImage: string;
  combineGift: boolean;
  endUnixTime: number;
  gifts: Gift[];
}

type AttributeFormat =
  | 'KEY_VALUE_TEXT'
  | 'RICH_TEXT_EDITOR'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'NUMBER'
  | 'PLAIN_TEXT'
  | 'NUMBER_RANGE';

export interface ProductAttribute {
  label: string;
  attributeType: string;
  isHighlighted: boolean;
  attributeFormat: AttributeFormat;
  additionalInformation: string;
  description: string;
  emptyValueText: string;
  prefix: string;
  prepopulated: string;
  prepopulatedFrom: string;
  suffix: string;
  value?: string;
  rangeValue?: {
    maximum: string;
    minimum: string;
  };
  startingRangePreposition?: string;
  keyValue?: KeyValue[];
  rawValue?: boolean;
}

interface KeyValue {
  key: string;
  values: string[];
}

interface CarInsuranceProvider {
  carrierCode: string;
  entryUid: string;
  providerImage: string;
  providerName: string;
  providerUid: string;
  urlAlias: string;
}

interface CallToActionLink {
  label: string;
  redirectedUrl: string;
  trackingSourceType: string;
}

export interface CarInsuranceProduct {
  entryUid: string;
  bestForInfo?: {
    id: string;
    title: string;
    keyBenefits: string[];
  };
  productName: string;
  description: string;
  longDescription: string;
  productImage: string;
  productUid: string;
  categoryIdentifiers: string[];
  cta: {
    title: string;
    links: CallToActionLink[];
  };
  provider: CarInsuranceProvider;
  urlAlias: string;
  attributeList: ProductAttribute[];
  exclusiveOffers: ExclusiveOffer[];
  welcomeOffers: ExclusiveOffer[];
  leadFormUrlAlias: string;
  badges: ProductBadge[];
}

export interface ProductPremium {
  brokerProductCode: string;
  carrierName: string;
  coverageType: string;
  discountValue: number;
  duePremium: number;
  maxExcess: number;
  mileagePremium: number;
  minExcess: number;
  originalPremium: number;
  planCode: string;
  planDesc: string;
  planProductCode: string;
  planName: string;
  standardExcess: string;
}

export interface CarInsurancePremiumedProduct extends CarInsuranceProduct {
  premium?: ProductPremium;
  highlight?: boolean;
}

export type VehicleModel = {
  baseModelName: string | undefined;
  carrierModelId: string | undefined;
  makeName: string;
  mgfYear: number | undefined;
  modelId: string;
  modelName: string;
  trim: string | undefined;
};

export type VehicleTrim = {
  modelId: string;
  trim: string;
};

export type Provider = {
  carrierCode: string | undefined;
  entryUid: string | undefined;
  providerImage: string | undefined;
  providerName: string | undefined;
  providerUid: string | undefined;
  urlAlias: string | undefined;
};

export enum Sort {
  Recommended = 'Recommended',
  Price = 'Price',
  Discount = 'Discount',
  Deductible = 'Deductible',
}

export type SortType = `${Sort}`;

export type CarLeadProps = {
  name: string;
  carMake: string;
  carModel: string;
  carTrim?: string;
  year: string;
  sex: string;
  maritalStatus: string;
  occupationType?: string;
  offPeak?: string;
  ncd: string;
  offenceFree?: string;
  claims: string;
  contact: string;
  contactType: ContactType;
  agreeReceiveMarketing: boolean;
  enquirePurpose: string;
  carInsuranceMonth?: string;
};

export enum ContactType {
  WA,
  Phone,
  Email,
}

export type PolicySearch = {
  title: string;
  titleMobile: string;
  subtitle: string;
  subtitleMobile: string;
};

export type CarInsuranceText = {
  title: string;
  subtitle: string;
  content: string;
};

export type CoverageContent = {
  title: string;
  icon: string;
  content: string;
};

export type QualityCoverage = {
  title: string;
  subtitle: string;
  content: CoverageContent[];
};

export type Step = {
  title: string;
  subtitle: string;
  icon: string;
};

export type Stepper = {
  title: string;
  subtitle: string;
  show: boolean;
  items: Step[];
};

export type FAQQuestionPlain = {
  question: string;
  hint?: string;
  type: 'plain_text';
  answer: {
    text: string;
  }[];
  footer?: {
    content: string;
    link: {
      text: string;
      href: string;
    };
  };
};

export type FAQQuestionTopics = {
  question: string;
  hint: string;
  type: 'topics';
  answer: {
    title: string;
    text: string;
  }[];
  footer?: {
    content: string;
    link: {
      text: string;
      href: string;
    };
  };
};

export type FAQQuestionTable = {
  question: string;
  hint: string;
  type: 'table';
  answer: {
    column_headers: string[];
    values: Record<string, string>[];
    text?: string;
    collapsetocard: boolean;
  }[];
  footer: {
    content: string;
    link: {
      text: string;
      href: string;
    };
  };
};

export type FAQQuestionEnumeration = {
  question: string;
  hint: string;
  type: 'enumeration';
  enumType?: 'multi' | 'single';
  answer: EnumAnswerType[];
  footer?: {
    content: string;
    link: {
      text: string;
      href: string;
    };
  };
};

export type EnumAnswerType = {
  text: string;
  subanswers?: { text: string }[];
};

export type FAQQuestion =
  | FAQQuestionPlain
  | FAQQuestionTopics
  | FAQQuestionTable
  | FAQQuestionEnumeration;

export type FAQ = {
  title: string;
  subtitle: string;
  questions: FAQQuestion[];
  show: boolean;
};

export type Tip = {
  title: string;
  icon: string;
  badge: string;
  link: string;
};

export type TipsAndArticles = {
  title: string;
  subtitle: string;
  content: Tip[];
};

export type CarInsuranceObject = {
  policySearch: PolicySearch;
  carInsurance: CarInsuranceText;
  qualityCoverage: QualityCoverage;
  stepper: Stepper;
  faq: FAQ;
  tipsAndArticles: TipsAndArticles;
};

export type RequestParams = {
  vehicle: {
    brandName: string;
    modelCode: string;
    manufactureYear: number;
  };
  mainDriver: {
    identityType: string;
    dateOfBirth: string;
    age: number;
    gender: string;
    maritalStatus: string;
    totalDrivingExperience: number;
    numberOfClaims: number;
    email: string;
    identityNumber: string;
    phoneNumber: string;
  };
  ncd: number;
};

export type Brand = 'singsaver' | 'moneyhero' | 'moneymax';

export type ProductReward = {
  title: string;
  endDate: string;
  description: string;
  gifts: Gift[];
  includeCountdownTimer?: boolean;
};
export type Article = {
  badge: string;
  icon: string;
  link: string;
  title: string;
  show: boolean;
};

export type ArticlesBlock = {
  title: string;
  subtitle: string;
  show: boolean;
  items: Article[];
};

export type GuidesBlock = {
  show: boolean;
  subtitle: string;
  title: string;
  guide: {
    title: string;
    text: string;
  }[];
};

export type ContactMe = {
  email: { email: string; caption: string; note: string };
  phone: { number: string; caption: string; note: string };
  whatsapp: {
    number: string;
    caption: string;
    message_template: string;
    message_template_empty: string;
  };
  show: boolean;
  title: string;
};

export type IconBlock = {
  title: string;
  subtitle: string;
  show: boolean;
  items: {
    title: string;
    content: string;
    icon: string;
  }[];
};

export type ProductBadge = {
  text: string;
  color: string;
  show_details: boolean;
  caption: string;
  details: string;
};

export type ContentStackProduct = {
  show: boolean;
  product_code: string;
  product_id: string;
  badges: ProductBadge[];
};

export type ContentStackProvider = {
  show: boolean;
  provider_code: string;
  provider_id: string;
};

export type Specialists = {
  contact: { number: string; caption: string };
  personas: { name: string; bio: string; photo: string }[];
  show: boolean;
  title: string;
  subtitle: string;
};

export type HeroBlock = {
  desktop: {
    title: string;
    subtitle: string;
  };
  mobile: {
    title: string;
    subtitle: string;
  };
};

export type SubHeroBlock = {
  title: string;
  subtitle: string;
  content: string;
  show: boolean;
};

export type PopularCarMakes = { title: string; displayPosition: number }[];

export type MoneyMaxProductPremium = {
  deductible: number;
  fmv: number;
  netPremium: number;
  prices: {
    actOfNature: { coverage: number; price: number };
    bodilyInjury: { coverage: number; price: number };
    ownDamageTheft: { coverage: number; price: number };
    personalAccident: { coverage: number; price: number };
    propertyDamage: { coverage: number; price: number };
  };
  productId: string;
  productName: string;
  providerId: string;
  providerName: string;
  taxes: {
    document_stamps: { percentage: string; price: number };
    localGovernment: { percentage: string; price: number };
    vat: { percentage: string; price: number };
  };
  totalPremium: number;
};

export type MoneyMaxPremiumedProduct = CarInsuranceProduct & {
  premium?: MoneyMaxProductPremium;
};

export type ReviewsBlock = {
  title: string;
  subtitle: string;
  show: boolean;
  review: {
    title: string;
    text: string;
    name: string;
    image: string;
    source: string;
  }[];
};
